import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiMessageCircle, FiUserX, FiCheck, FiArrowUp
} from 'react-icons/fi';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

function ITDashboard({ user, dashboardData }) {
  // Debug: Log the ratings data
  React.useEffect(() => {
    if (dashboardData?.ratings) {
      console.log('Ratings data:', dashboardData.ratings);
    }
  }, [dashboardData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div style={{background: 'var(--gradient-primary)'}} className="rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-white">IT Operations Dashboard</h1>
            <p className="text-white opacity-90">Real-time system monitoring and ticket management</p>
          </div>
          <div className="hidden md:block">
            <div className="rounded-lg p-4">
              <div className="text-white text-sm opacity-75">Welcome back,</div>
              <div className="text-white font-semibold">{user?.user_full_name}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-primary)'}}>{dashboardData.tickets?.open || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Open Tickets</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#e3f2fd'}}>
              <FiMessageCircle className="w-6 h-6" style={{color: '#0086D4'}} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-danger)'}}>{dashboardData.tickets?.not_assigned || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Unassigned</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#f8d7da'}}>
              <FiUserX className="w-6 h-6" style={{color: '#dc3545'}} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-success)'}}>{dashboardData.tickets?.closed || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Completed</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#d4edda'}}>
              <FiCheck className="w-6 h-6" style={{color: '#28a745'}} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-accent)'}}>{dashboardData.tickets?.provider || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Escalated</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#f5f5f5'}}>
              <FiArrowUp className="w-6 h-6" style={{color: '#B0796D'}} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Monthly Ticket Trends - full width */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Monthly Ticket Trends</h3>
          </div>
          <div className="p-4">
            {(() => {
              const monthly = dashboardData.monthly_ticket_stats || [];
              const labels = monthly.map(m => m.label);
              const opened = monthly.map(m => m.opened);
              const closed = monthly.map(m => m.closed);
              const pending = monthly.map(m => m.pending);
              const total = monthly.map(m => m.total);
              return (
                <Bar
                  data={{
                    labels,
                    datasets: [
                      {
                        label: 'opened',
                        data: opened,
                        backgroundColor: 'rgba(34, 197, 94, 0.85)',
                        borderRadius: 3,
                        order: 1
                      },
                      {
                        label: 'closed',
                        data: closed,
                        backgroundColor: 'rgba(78, 173, 226, 0.85)',
                        borderRadius: 3,
                        order: 1
                      },
                      {
                        label: 'pending',
                        data: pending,
                        backgroundColor: 'rgba(234, 179, 8, 0.85)',
                        borderRadius: 3,
                        order: 1
                      },
                      {
                        label: 'Total',
                        data: total,
                        backgroundColor: 'rgba(21, 128, 61, 0.85)',
                        borderRadius: 3,
                        order: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { usePointStyle: true, pointStyle: 'rect', padding: 20, font: { size: 12 } }
                      },
                      tooltip: { mode: 'index', intersect: false },
                      datalabels: { display: false }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(0,0,0,0.06)' },
                        ticks: { font: { size: 11 } }
                      },
                      x: {
                        grid: { display: false },
                        ticks: { font: { size: 11 } }
                      }
                    }
                  }}
                  height={300}
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* Additional Chat Metrics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Categories Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Chat Channel Activity</h3>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {(() => {
                const categoryData = dashboardData.chat_volume_by_category || [];
                
                const webData = categoryData.find(cat => cat.category === 1) || {total_chats: 0, answered_chats: 0};
                const whatsappData = categoryData.find(cat => cat.category === 2) || {total_chats: 0, answered_chats: 0};
                const helpdeskData = categoryData.find(cat => cat.category === 3) || {total_chats: 0, answered_chats: 0};
                
                const webPercentage = webData.total_chats > 0 ? Math.round((webData.answered_chats / webData.total_chats) * 100) : 0;
                const whatsappPercentage = whatsappData.total_chats > 0 ? Math.round((whatsappData.answered_chats / whatsappData.total_chats) * 100) : 0;
                const helpdeskPercentage = helpdeskData.total_chats > 0 ? Math.round((helpdeskData.answered_chats / helpdeskData.total_chats) * 100) : 0;
                
                return (
                  <>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span style={{color: 'var(--text-primary)'}}>Web Chat ({webData.total_chats})</span>
                        <span style={{color: 'var(--color-info)'}}>{webPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full bg-blue-500" style={{width: `${webPercentage}%`}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span style={{color: 'var(--text-primary)'}}>WhatsApp ({whatsappData.total_chats})</span>
                        <span style={{color: 'var(--color-success)'}}>{whatsappPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full bg-green-500" style={{width: `${whatsappPercentage}%`}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span style={{color: 'var(--text-primary)'}}>Helpdesk ({helpdeskData.total_chats})</span>
                        <span style={{color: 'var(--color-warning)'}}>{helpdeskPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full bg-purple-500" style={{width: `${helpdeskPercentage}%`}}></div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Chat Queue Status */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Pending Chat Requests</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{color: 'var(--color-warning)'}}>{(dashboardData.missed_support_chats || []).length}</div>
                <div className="text-xs" style={{color: 'var(--text-secondary)'}}>In Queue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{color: 'var(--color-info)'}}>{dashboardData.avg_resolution_time || 0}m</div>
                <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Avg Wait</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t" style={{borderColor: 'var(--border-light)'}}>
              <div className="text-center">
                <div className="text-lg font-bold" style={{color: 'var(--color-success)'}}>{dashboardData.support_conversations || 0}</div>
                <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Active Chats</div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Satisfaction */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Today's Chat Overview</h3>
          </div>
          <div className="p-4">
            <div className="text-center mb-4">
              <div className="text-3xl font-bold" style={{color: 'var(--color-success)'}}>{dashboardData.chat_response_rate || 0}%</div>
              <div className="text-sm" style={{color: 'var(--text-secondary)'}}>Response Rate</div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span style={{color: 'var(--text-secondary)'}}>Total Volume</span>
                <span style={{color: 'var(--color-primary)'}}>{dashboardData.total_chat_volume || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{color: 'var(--text-secondary)'}}>Missed</span>
                <span style={{color: 'var(--color-danger)'}}>{(dashboardData.missed_support_chats || []).length}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{color: 'var(--text-secondary)'}}>Resolved</span>
                <span style={{color: 'var(--color-success)'}}>{Math.max(0, (dashboardData.answered_chats || dashboardData.total_chats || dashboardData.total_chat_volume || 0) - (dashboardData.missed_support_chats || []).length)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Satisfaction Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ratings Summary Card */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b flex items-center justify-between" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Customer Satisfaction</h3>
            <Link 
              to="/dashboard/helpdesk/ratings" 
              className="text-xs hover:underline"
              style={{color: 'var(--color-primary)'}}
            >
              View All
            </Link>
          </div>
          <div className="p-4">
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-8 h-8" style={{color: '#F59E0B'}} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <div className="text-3xl font-bold" style={{color: 'var(--color-primary)'}}>
                  {dashboardData.ratings?.average || '—'}/10
                </div>
              </div>
              <div className="text-sm" style={{color: 'var(--text-secondary)'}}>Average Rating</div>
            </div>
            <div className="space-y-2 pt-3 border-t" style={{borderColor: 'var(--border-light)'}}>
              <div className="flex justify-between text-sm">
                <span style={{color: 'var(--text-secondary)'}}>Total Ratings</span>
                <span className="font-medium" style={{color: 'var(--color-primary)'}}>{dashboardData.ratings?.count || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{color: 'var(--text-secondary)'}}>Response Rate</span>
                <span className="font-medium" style={{color: 'var(--color-success)'}}>{dashboardData.ratings?.response_rate || 0}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{color: 'var(--text-secondary)'}}>This Month</span>
                <span className="font-medium" style={{color: 'var(--color-accent)'}}>{dashboardData.ratings?.this_month || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ratings Distribution */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Rating Distribution</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {[
                { stars: '9-10', color: 'bg-green-500', count: dashboardData.ratings?.distribution?.excellent || 0 },
                { stars: '7-8', color: 'bg-blue-500', count: dashboardData.ratings?.distribution?.good || 0 },
                { stars: '5-6', color: 'bg-yellow-500', count: dashboardData.ratings?.distribution?.average || 0 },
                { stars: '1-4', color: 'bg-red-500', count: dashboardData.ratings?.distribution?.poor || 0 }
              ].map((item, index) => {
                const total = dashboardData.ratings?.count || 1;
                const percentage = Math.round((item.count / total) * 100) || 0;
                return (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span style={{color: 'var(--text-primary)'}}>{item.stars} stars ({item.count})</span>
                      <span style={{color: 'var(--color-info)'}}>{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full ${item.color}`} style={{width: `${percentage}%`}}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Recent Tickets - 2 columns */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
            <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
              <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Latest Support Requests</h3>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {(dashboardData.tickets_all || []).slice(0, 6).map((ticket, index) => {
                  let statusColor = 'var(--color-info)';
                  let statusText = 'Open';
                  
                  if (ticket.ticket_status === 'closed' || ticket.ticket_status === 'resolved' || ticket.ticket_status === 'completed') {
                    statusColor = 'var(--color-success)';
                    statusText = 'Closed';
                  } else if (ticket.ticket_status === 'escalated') {
                    statusColor = 'var(--color-accent)';
                    statusText = 'Escalated';
                  } else if (!ticket.assigned_to) {
                    statusColor = 'var(--color-danger)';
                    statusText = 'Unassigned';
                  } else if (ticket.ticket_status === 'pending_approval') {
                    statusColor = 'var(--color-warning)';
                    statusText = 'Pending Approval';
                  } else if (ticket.ticket_status === 'approved') {
                    statusColor = 'var(--color-info)';
                    statusText = 'Approved';
                  } else if (ticket.ticket_status === 'in_progress') {
                    statusColor = 'var(--color-info)';
                    statusText = 'In Progress';
                  }
                  
                  return (
                    <Link 
                      key={index} 
                      to={`/dashboard/helpdesk?ticket=${ticket.ticket_id}`}
                      className="flex items-center justify-between p-3 rounded hover:bg-gray-100 transition-colors" 
                      style={{backgroundColor: 'var(--bg-secondary)'}}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm" style={{color: 'var(--color-primary)'}}>{ticket.ticket_number}</div>
                        <div className="text-sm truncate" style={{color: 'var(--text-primary)'}}>{ticket.title}</div>
                        <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{ticket.user_full_name}</div>
                      </div>
                      <div className="text-xs px-2 py-1 rounded" style={{backgroundColor: statusColor, color: 'white'}}>
                        {statusText}
                      </div>
                    </Link>
                  );
                })}
                {(!dashboardData.tickets_all || dashboardData.tickets_all.length === 0) && (
                  <div className="text-center py-8" style={{color: 'var(--text-secondary)'}}>
                    No recent tickets found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Metrics */}
        <div className="space-y-6">
          {/* Chat Overview */}
          <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
            <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
              <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Chat Performance Metrics</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{color: 'var(--color-primary)'}}>{dashboardData.total_chats || dashboardData.total_chat_volume || 0}</div>
                  <div className="text-sm" style={{color: 'var(--text-secondary)'}}>Total Chats</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{color: 'var(--color-success)'}}>{dashboardData.chat_response_rate || 0}%</div>
                  <div className="text-sm" style={{color: 'var(--text-secondary)'}}>Response Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Missed Chats Alert */}
          <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
            <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
              <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Unanswered Chat Alerts</h3>
            </div>
            <div className="p-4">
              {(dashboardData.missed_support_chats || []).length > 0 ? (
                <div className="space-y-2">
                  <div className="text-center mb-3">
                    <div className="text-2xl font-bold" style={{color: 'var(--color-danger)'}}>{(dashboardData.missed_support_chats || []).length}</div>
                    <div className="text-sm" style={{color: 'var(--text-secondary)'}}>Missed Chats</div>
                  </div>
                  {(dashboardData.missed_support_chats || []).slice(0, 3).map((chat, index) => (
                    <div key={index} className="p-2 rounded" style={{backgroundColor: 'var(--bg-secondary)'}}>
                      <div className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>{chat.name}</div>
                      <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{chat.message_count} messages</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="text-2xl font-bold" style={{color: 'var(--color-success)'}}>0</div>
                  <div className="text-sm" style={{color: 'var(--text-secondary)'}}>No Missed Chats</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stacked: Response Times, System Status */}
        <div className="space-y-6">
          {/* Response Times */}
          <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
            <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
              <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Support Response Metrics</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold" style={{color: 'var(--color-info)'}}>{dashboardData.avg_response_time || dashboardData.avg_resolution_time || 0}h</div>
                  <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Avg Response</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold" style={{color: 'var(--color-primary)'}}>{dashboardData.support_conversations || 0}</div>
                  <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Active Chats</div>
                </div>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
            <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
              <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Service Health Monitor</h3>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {dashboardData.service_status && Object.entries(dashboardData.service_status).map(([service, status]) => (
                  <div key={service} className="flex items-center justify-between">
                    <span className="text-sm capitalize" style={{color: 'var(--text-primary)'}}>{service.replace('_', ' ')}</span>
                    <span className={`text-xs px-2 py-1 rounded ${status === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {status}
                    </span>
                  </div>
                ))}
                {dashboardData.system_uptime && (
                  <div className="pt-2 border-t" style={{borderColor: 'var(--border-light)'}}>
                    <div className="text-center">
                      <div className="text-lg font-bold" style={{color: 'var(--color-success)'}}>{dashboardData.system_uptime.uptime_percentage}%</div>
                      <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{dashboardData.system_uptime.uptime_days} days uptime</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Requisitions below Recent Tickets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Requisitions */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Purchase Requests</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {(dashboardData.all_requisitions || []).slice(0, 5).map((req, index) => {
                let statusColor = 'var(--color-warning)';
                let statusText = 'Pending';
                
                if (req.requisition_status >= req.specification_approvals) {
                  statusColor = 'var(--color-success)';
                  statusText = 'Approved';
                } else if (req.requisition_status > 0) {
                  statusColor = 'var(--color-info)';
                  statusText = 'Review';
                }
                
                return (
                  <Link 
                    key={index} 
                    to={`/dashboard/requisitions/${req.requisition_id}`}
                    className="block p-3 rounded hover:bg-gray-100 transition-colors" 
                    style={{backgroundColor: 'var(--bg-secondary)'}}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-sm" style={{color: 'var(--color-primary)'}}>EPR-{req.requisition_number}</div>
                      <div className="text-xs px-2 py-1 rounded" style={{backgroundColor: statusColor, color: 'white'}}>
                        {statusText}
                      </div>
                    </div>
                    <div className="text-sm truncate" style={{color: 'var(--text-primary)'}}>{req.specification_title}</div>
                    <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{req.user_full_name}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Software Licenses */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>License Expiry Tracking</h3>
          </div>
          <div className="p-4">
            <div className="space-y-2">
              {(dashboardData.software || []).slice(0, 5).map((software, index) => {
                const expiryDate = new Date(software.asset_expiry_date);
                const today = new Date();
                const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                
                let statusColor = 'var(--color-success)';
                let statusText = 'Active';
                
                if (daysUntilExpiry < 0) {
                  statusColor = 'var(--color-danger)';
                  statusText = 'Expired';
                } else if (daysUntilExpiry < 30) {
                  statusColor = 'var(--color-warning)';
                  statusText = 'Expiring Soon';
                }
                
                return (
                  <div key={index} className="p-2 rounded" style={{backgroundColor: 'var(--bg-secondary)'}}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{color: 'var(--text-primary)'}}>{software.asset_name}</div>
                        <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{software.vendor}</div>
                      </div>
                      <div className="text-xs px-2 py-1 rounded ml-2" style={{backgroundColor: statusColor, color: 'white'}}>
                        {statusText}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Performance below Requisitions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Metrics Chart */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Ticket Resolution Stats</h3>
          </div>
          <div className="p-4">
            <Pie
              data={{
                labels: ['Resolved', 'In Progress', 'Pending'],
                datasets: [
                  {
                    data: [65, 25, 10],
                    backgroundColor: [
                      '#28a745',
                      '#4EADE2',
                      '#ffc107'
                    ],
                    borderWidth: 0
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  },
                  datalabels: { display: false }
                }
              }}
              height={200}
            />
          </div>
        </div>

        {/* Resource Usage */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>System Resource Monitor</h3>
          </div>
          <div className="p-4">
            {dashboardData.resource_usage && (
              <div className="space-y-3">
                {Object.entries(dashboardData.resource_usage).map(([resource, usage]) => {
                  let color = 'var(--color-success)';
                  if (usage > 80) color = 'var(--color-danger)';
                  else if (usage > 60) color = 'var(--color-warning)';
                  
                  return (
                    <div key={resource}>
                      <div className="flex justify-between text-sm mb-1">
                        <span style={{color: 'var(--text-primary)'}} className="capitalize">{resource.replace('_', ' ')}</span>
                        <span style={{color}}>{usage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full transition-all duration-300" 
                          style={{width: `${usage}%`, backgroundColor: color}}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logged Tickets Per Department Monthly - full-featured pie chart */}
        <div className="bg-white rounded-lg shadow-sm border col-span-1 lg:col-span-2" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-5">
            <h3 className="text-base font-bold text-center mb-4" style={{color: 'var(--text-primary)'}}>Logged Tickets Per Department Monthly</h3>
            {dashboardData.tickets_by_department && dashboardData.tickets_by_department.length > 0 ? (
              <div className="flex items-center justify-center gap-8">
                <div style={{width: 280, height: 280, flexShrink: 0}}>
                  <Pie
                    data={{
                      labels: dashboardData.tickets_by_department.map(item => item.name),
                      datasets: [
                        {
                          data: dashboardData.tickets_by_department.map(item => item.value),
                          backgroundColor: [
                            '#1a3a5c',
                            '#e07b39',
                            '#2e7d32',
                            '#29b6c4',
                            '#8e24aa',
                            '#558b2f',
                            '#1565c0',
                            '#b71c1c',
                            '#f9a825',
                            '#37474f'
                          ],
                          borderWidth: 2,
                          borderColor: '#fff'
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const value = context.parsed || 0;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const pct = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                              return `${context.label}: ${value} (${pct}%)`;
                            }
                          }
                        },
                        datalabels: {
                          color: '#fff',
                          font: { weight: 'bold', size: 13 },
                          formatter: (value, context) => {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                            return pct >= 5 ? `${pct}%` : '';
                          }
                        }
                      }
                    }}
                  />
                </div>
                {/* Custom right-side legend */}
                <div className="flex flex-col gap-2">
                  {(() => {
                    const colors = ['#1a3a5c','#e07b39','#2e7d32','#29b6c4','#8e24aa','#558b2f','#1565c0','#b71c1c','#f9a825','#37474f'];
                    const total = dashboardData.tickets_by_department.reduce((sum, item) => sum + item.value, 0);
                    return dashboardData.tickets_by_department.map((item, idx) => {
                      const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="inline-block rounded-sm flex-shrink-0" style={{width:14, height:14, backgroundColor: colors[idx % colors.length]}}></span>
                          <span className="text-xs flex-1" style={{color:'var(--text-primary)'}}>{item.name}</span>
                          <span className="text-xs font-medium ml-2" style={{color:'var(--text-secondary)'}}>{item.value} <span style={{color: colors[idx % colors.length]}}>({pct}%)</span></span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            ) : (
              <div className="text-center py-12" style={{color: 'var(--text-secondary)'}}>No department data available</div>
            )}
          </div>
        </div>

        {/* Open Tickets by Division */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Open Tickets by Division</h3>
            <p className="text-xs mt-1" style={{color: 'var(--text-secondary)'}}>Distribution by division/sub-unit</p>
          </div>
          <div className="p-4">
            {dashboardData.tickets_by_division && dashboardData.tickets_by_division.length > 0 ? (
              <Pie
                data={{
                  labels: dashboardData.tickets_by_division.map(item => item.name),
                  datasets: [
                    {
                      data: dashboardData.tickets_by_division.map(item => item.value),
                      backgroundColor: [
                        '#28a745',
                        '#17a2b8',
                        '#ffc107',
                        '#dc3545',
                        '#6f42c1',
                        '#fd7e14',
                        '#20c997',
                        '#e83e8c',
                        '#6c757d',
                        '#007bff'
                      ],
                      borderWidth: 0
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        padding: 8,
                        font: {
                          size: 11
                        }
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.label || '';
                          const value = context.parsed || 0;
                          const total = context.dataset.data.reduce((a, b) => a + b, 0);
                          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                          return `${label}: ${value} (${percentage}%)`;
                        }
                      }
                    },
                    datalabels: { display: false }
                  }
                }}
                height={200}
              />
            ) : (
              <div className="text-center py-12" style={{color: 'var(--text-secondary)'}}>
                No division data available
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Original Secondary Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket Trends Chart */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Weekly Ticket Volume</h3>
          </div>
          <div className="p-4">
            <Line
              data={{
                labels: dashboardData.tickets_days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                datasets: [
                  {
                    label: 'Created',
                    data: dashboardData.tickets_total || [12, 19, 8, 15, 22, 8, 14],
                    borderColor: '#4EADE2',
                    backgroundColor: 'rgba(78, 173, 226, 0.1)',
                    fill: true,
                    tension: 0.4
                  },
                  {
                    label: 'Closed',
                    data: dashboardData.tickets_closed || [8, 15, 6, 12, 18, 5, 10],
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    fill: true,
                    tension: 0.4
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  },
                  datalabels: { display: false }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0,0,0,0.1)'
                    }
                  },
                  x: {
                    grid: {
                      display: false
                    }
                  }
                }
              }}
              height={200}
            />
          </div>
        </div>

        {/* Ticket Status Distribution */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Ticket Status Breakdown</h3>
          </div>
          <div className="p-4">
            <Doughnut
              data={{
                labels: ['Open', 'Unassigned', 'Closed', 'Escalated'],
                datasets: [
                  {
                    data: [
                      dashboardData.tickets?.open || 0,
                      dashboardData.tickets?.not_assigned || 0,
                      dashboardData.tickets?.closed || 0,
                      dashboardData.tickets?.provider || 0
                    ],
                    backgroundColor: [
                      '#0086D4',
                      '#B0796D',
                      '#28a745',
                      '#4EADE2'
                    ],
                    borderWidth: 0
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  },
                  datalabels: { display: false }
                }
              }}
              height={200}
            />
          </div>
        </div>

        {/* Department Ticket Statistics Table */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Department Ticket Summary</h3>
            <p className="text-xs mt-1" style={{color: 'var(--text-secondary)'}}>Key metrics by department</p>
          </div>
          <div className="p-4">
            {dashboardData.ticket_stats_by_department && dashboardData.ticket_stats_by_department.length > 0 ? (
              <div className="overflow-y-auto" style={{maxHeight: '200px'}}>
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b" style={{borderColor: 'var(--border-light)'}}>
                      <th className="text-left pb-2" style={{color: 'var(--text-primary)'}}>Department</th>
                      <th className="text-center pb-2" style={{color: 'var(--text-primary)'}}>Open</th>
                      <th className="text-center pb-2" style={{color: 'var(--text-primary)'}}>Closed</th>
                      <th className="text-center pb-2" style={{color: 'var(--color-danger)'}}>Urgent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.ticket_stats_by_department.slice(0, 8).map((dept, index) => (
                      <tr key={index} className="border-b" style={{borderColor: 'var(--border-light)'}}>
                        <td className="py-2 pr-2" style={{color: 'var(--text-primary)'}}>{dept.department_name}</td>
                        <td className="text-center py-2" style={{color: 'var(--color-info)'}}>{dept.open_tickets}</td>
                        <td className="text-center py-2" style={{color: 'var(--color-success)'}}>{dept.closed_tickets}</td>
                        <td className="text-center py-2" style={{color: 'var(--color-danger)'}}>{dept.urgent_tickets}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12" style={{color: 'var(--text-secondary)'}}>
                No statistics available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ITDashboard;
